<template>
  <button
    type="button"
    class="qr-help-btn inline-flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60 focus-visible:ring-offset-1"
    :class="btnClass"
    :title="content?.title ? `查看说明：${content.title}` : '点击查看说明'"
    :aria-label="content?.title ? `${content.title} 说明` : '查看配置说明'"
    @click.stop="open = true"
  >
    <svg viewBox="0 0 20 20" fill="currentColor" :class="iconClass" aria-hidden="true">
      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
    </svg>
  </button>

  <Teleport to="body">
    <Transition name="qr-help-fade">
      <div v-if="open" class="fixed inset-0 z-[9999] flex items-center justify-center p-4" @click.self="open = false">
        <div class="absolute inset-0 bg-black/45 backdrop-blur-[1px]" @click="open = false"></div>
        <Transition name="qr-help-scale" appear>
          <div v-if="open" class="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden" @click.stop>
            <div class="px-5 py-4 border-b border-gray-100 flex items-start gap-3 bg-gradient-to-r from-blue-50/80 to-white">
              <div class="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="flex-1 min-w-0 pr-2">
                <h3 class="text-base font-semibold text-gray-900 leading-snug">{{ content?.title || '配置说明' }}</h3>
                <p class="text-xs text-gray-500 mt-0.5">用途与用法说明</p>
              </div>
              <button type="button" class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1 flex-shrink-0 transition-colors" @click="open = false">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div class="px-5 py-4 overflow-y-auto space-y-4 text-sm text-gray-700">
              <div v-if="content?.purpose" class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5">
                <p class="text-xs font-semibold text-blue-600 mb-1.5 flex items-center gap-1">
                  <span class="w-1 h-3 rounded-full bg-blue-500"></span>用途
                </p>
                <p class="leading-relaxed whitespace-pre-line text-gray-700">{{ content.purpose }}</p>
              </div>
              <div v-if="content?.usage" class="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5">
                <p class="text-xs font-semibold text-blue-600 mb-1.5 flex items-center gap-1">
                  <span class="w-1 h-3 rounded-full bg-blue-500"></span>用法
                </p>
                <p class="leading-relaxed whitespace-pre-line text-gray-700">{{ content.usage }}</p>
              </div>
              <div v-if="content?.tips" class="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5">
                <p class="text-xs font-semibold text-amber-700 mb-1.5 flex items-center gap-1">
                  <span class="w-1 h-3 rounded-full bg-amber-500"></span>提示
                </p>
                <p class="leading-relaxed whitespace-pre-line text-amber-900/80">{{ content.tips }}</p>
              </div>
              <p v-if="!content" class="text-gray-400 text-center py-4">暂无说明</p>
            </div>
            <div class="px-5 py-3 border-t border-gray-100 flex justify-end bg-gray-50/50">
              <button type="button" class="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm transition-colors" @click="open = false">知道了</button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getHelpContent } from './configHelpContent'

const props = defineProps({
  helpKey: { type: String, required: true },
  size: { type: String, default: 'md' },
})

const open = ref(false)
const content = computed(() => getHelpContent(props.helpKey))

const btnClass = computed(() => {
  if (props.size === 'lg') return 'w-5 h-5 bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:border-blue-400 hover:text-blue-700 hover:shadow-sm active:scale-95'
  if (props.size === 'sm') return 'w-4 h-4 bg-slate-50 text-slate-500 border border-slate-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 hover:shadow-sm active:scale-95'
  return 'w-[18px] h-[18px] bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:border-blue-400 hover:text-blue-700 hover:shadow-sm active:scale-95'
})

const iconClass = computed(() => {
  if (props.size === 'lg') return 'w-3.5 h-3.5'
  if (props.size === 'sm') return 'w-2.5 h-2.5'
  return 'w-3 h-3'
})
</script>

<style scoped>
.qr-help-fade-enter-active,
.qr-help-fade-leave-active {
  transition: opacity 0.18s ease;
}
.qr-help-fade-enter-from,
.qr-help-fade-leave-to {
  opacity: 0;
}
.qr-help-scale-enter-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.qr-help-scale-leave-active {
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.qr-help-scale-enter-from,
.qr-help-scale-leave-to {
  transform: scale(0.96) translateY(4px);
  opacity: 0;
}
</style>
